import { useCallback, useMemo, useState } from "react";
import { Archive, ArchiveRestore, Plus, Pencil, Trash2 } from "lucide-react";
import { useTasks } from "../hooks/useTasks";
import { useTokens } from "../hooks/useTokens";
import { WalletRegistryStatus } from "../components/WalletRegistryStatus";
import { TaskRow } from "../components/TaskRow";
import { TaskEditor } from "../components/TaskEditor";
import { TokenEditor } from "../components/TokenEditor";
import { ChainBadge } from "../components/ChainBadge";
import { ApiError } from "../api/client";
import type { Task, Token } from "../api/types";

function TokenFdv({ value }: { value: number | null | undefined }): JSX.Element {
  return (
    <div className="shrink-0 text-right">
      <div className="text-[9px] uppercase tracking-nav text-muted">FDV</div>
      <div className="font-mono text-xs">
        {value !== null && value !== undefined
          ? `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
          : "—"}
      </div>
    </div>
  );
}

type TaskEditorState =
  | { kind: "closed" }
  | { kind: "create"; tokenId?: string }
  | { kind: "edit"; task: Task };

type TokenEditorState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; token: Token };

export function Tasks(): JSX.Element {
  const { tasks, loading, error: tasksError, create: createTask, update: updateTask, remove: removeTask, start, stop } = useTasks();
  const {
    tokens,
    loading: tokensLoading,
    error: tokensError,
    create: createToken,
    update: updateToken,
    remove: removeToken,
  } = useTokens();

  const [taskEditor, setTaskEditor] = useState<TaskEditorState>({ kind: "closed" });
  const [tokenEditor, setTokenEditor] = useState<TokenEditorState>({ kind: "closed" });
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [fdvByTask, setFdvByTask] = useState<Record<string, number | null>>({});

  const handleFdvChange = useCallback((taskId: string, fdvUsd: number | null) => {
    setFdvByTask((current) =>
      current[taskId] === fdvUsd ? current : { ...current, [taskId]: fdvUsd },
    );
  }, []);

  // Group tasks by tokenId. Tokens with no tasks are appended (useful for
  // freshly-added tokens before the user creates their first task).
  const groups = useMemo(() => {
    const byTokenId = new Map<string, Task[]>();
    for (const t of tasks) {
      if (!t.tokenId) continue;
      const list = byTokenId.get(t.tokenId);
      if (list) list.push(t);
      else byTokenId.set(t.tokenId, [t]);
    }
    return tokens
      .map((token) => ({ token, items: byTokenId.get(token.id) ?? [] }))
      .sort((a, b) => b.token.createdAt - a.token.createdAt);
  }, [tasks, tokens]);

  const orphanedTasks = useMemo(
    () => tasks.filter((t) => !t.tokenId || !tokens.some((tk) => tk.id === t.tokenId)),
    [tasks, tokens],
  );
  const activeGroups = groups.filter(({ token }) => !token.archived);
  const archivedGroups = groups.filter(({ token }) => token.archived);

  async function handleDeleteToken(token: Token): Promise<void> {
    setTokenError(null);
    if (!confirm(`Delete token "${token.name}"? Its tasks must already be deleted.`)) return;
    try {
      await removeToken(token.id);
    } catch (err) {
      setTokenError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Delete failed");
    }
  }

  async function handleArchiveToken(token: Token, archived: boolean): Promise<void> {
    setTokenError(null);
    if (archived) {
      if (!confirm(`Move "${token.name}" to archive? Its stopped tasks will be kept.`)) return;
    }
    try {
      await updateToken(token.id, { archived });
    } catch (err) {
      setTokenError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Archive update failed");
    }
  }

  return (
    <main className="max-w-[1400px] mx-auto px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-[32px] leading-none">Market Making</h2>
          <p className="text-xs text-muted mt-1 uppercase tracking-nav">
            Counter-trade strategies grouped by token
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchive((value) => !value)}
            className="btn btn-ghost"
          >
            <Archive size={14} strokeWidth={1.5} />
            Archive ({archivedGroups.length})
          </button>
          <button
            onClick={() => setTokenEditor({ kind: "create" })}
            className="btn btn-ghost"
          >
            <Plus size={14} strokeWidth={1.5} />
            New token
          </button>
        </div>
      </div>

      <WalletRegistryStatus />

      {(tasksError || tokensError || tokenError) && (
        <div className="mb-4 border border-err p-3 text-xs text-err">
          {tokenError ?? tokensError ?? tasksError}
        </div>
      )}

      {(loading || tokensLoading) && tasks.length === 0 && tokens.length === 0 ? (
        <div className="card p-16 text-center text-muted text-sm">Loading…</div>
      ) : activeGroups.length === 0 && archivedGroups.length === 0 && orphanedTasks.length === 0 ? (
        <div className="card p-16 text-center">
          <p className="text-muted text-sm">
            No tokens yet. Click <span className="text-fg">New token</span> to add one,
            then create tasks under it.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {activeGroups.length === 0 && !showArchive && archivedGroups.length > 0 && (
            <div className="card p-10 text-center text-sm text-muted">
              No active clients. Open Archive to view {archivedGroups.length} saved client{archivedGroups.length === 1 ? "" : "s"}.
            </div>
          )}
          {activeGroups.map(({ token, items }) => (
            <section key={token.id} className="border border-border">
              <div className="flex items-center gap-3 px-4 py-3 bg-hover border-b border-border">
                <ChainBadge chain={token.chain} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-base">{token.name}</span>
                    <span className="font-mono text-[11px] text-muted truncate">
                      {token.mint}
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-nav text-muted mt-0.5">
                    {token.pools.length} pool{token.pools.length === 1 ? "" : "s"} ·{" "}
                    {items.length} task{items.length === 1 ? "" : "s"}
                  </div>
                </div>
                <TokenFdv
                  value={items
                    .filter((task) => task.status === "running")
                    .map((task) => fdvByTask[task.id])
                    .find((fdv): fdv is number => typeof fdv === "number")}
                />
                <div className="flex items-center gap-2">
                  <button
                    className="btn btn-ghost px-2 py-1 text-[10px]"
                    onClick={() => setTaskEditor({ kind: "create", tokenId: token.id })}
                  >
                    <Plus size={12} strokeWidth={1.5} />
                    Add task
                  </button>
                  <button
                    className="btn btn-ghost px-2 py-1 text-[10px]"
                    onClick={() => setTokenEditor({ kind: "edit", token })}
                    title="Edit token (name + pools)"
                  >
                    <Pencil size={12} strokeWidth={1.5} />
                  </button>
                  <button
                    className="btn btn-ghost px-2 py-1 text-[10px]"
                    onClick={() => void handleArchiveToken(token, true)}
                    disabled={items.some((task) => task.status === "running")}
                    title={
                      items.some((task) => task.status === "running")
                        ? "Stop every task before archiving"
                        : "Move client to archive"
                    }
                  >
                    <Archive size={12} strokeWidth={1.5} />
                  </button>
                  <button
                    className="btn btn-ghost px-2 py-1 text-[10px] text-err"
                    onClick={() => void handleDeleteToken(token)}
                    title={
                      items.length > 0
                        ? "Delete tasks first to remove this token"
                        : "Delete token"
                    }
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {items.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-muted">
                  No tasks yet for this token.{" "}
                  <button
                    className="text-fg underline decoration-dotted hover:opacity-80"
                    onClick={() => setTaskEditor({ kind: "create", tokenId: token.id })}
                  >
                    Add one
                  </button>
                  .
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {items.map((t) => (
                    <TaskRow
                      key={t.id}
                      task={t}
                      onStart={() => start(t.id)}
                      onStop={() => stop(t.id)}
                      onEdit={() => setTaskEditor({ kind: "edit", task: t })}
                      onDelete={() => removeTask(t.id)}
                      onFdvChange={handleFdvChange}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}

          {showArchive && archivedGroups.length > 0 && (
            <>
              <div className="flex items-center gap-2 pt-2 text-xs uppercase tracking-nav text-muted">
                <Archive size={14} strokeWidth={1.5} />
                Archived clients
              </div>
              {archivedGroups.map(({ token, items }) => (
                <section key={token.id} className="border border-border opacity-80">
                  <div className="flex items-center gap-3 px-4 py-3 bg-hover border-b border-border">
                    <ChainBadge chain={token.chain} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-serif text-base">{token.name}</span>
                        <span className="border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-nav text-muted">
                          archived
                        </span>
                        <span className="font-mono text-[11px] text-muted truncate">
                          {token.mint}
                        </span>
                      </div>
                      <div className="text-[10px] uppercase tracking-nav text-muted mt-0.5">
                        {token.pools.length} pool{token.pools.length === 1 ? "" : "s"} ·{" "}
                        {items.length} task{items.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="btn btn-ghost px-2 py-1 text-[10px]"
                        onClick={() => void handleArchiveToken(token, false)}
                        title="Restore client"
                      >
                        <ArchiveRestore size={12} strokeWidth={1.5} />
                        Restore
                      </button>
                      <button
                        className="btn btn-ghost px-2 py-1 text-[10px] text-err"
                        onClick={() => void handleDeleteToken(token)}
                        title={items.length > 0 ? "Delete tasks first to remove this token" : "Delete token"}
                      >
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  {items.length === 0 ? (
                    <div className="px-4 py-5 text-center text-xs text-muted">No tasks saved.</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {items.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          archived
                          onStart={() => start(task.id)}
                          onStop={() => stop(task.id)}
                          onEdit={() => setTaskEditor({ kind: "edit", task })}
                          onDelete={() => removeTask(task.id)}
                        />
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </>
          )}

          {orphanedTasks.length > 0 && (
            <section className="border border-warn">
              <div className="px-4 py-3 bg-hover border-b border-warn">
                <div className="text-warn text-xs uppercase tracking-nav">
                  Orphaned tasks ({orphanedTasks.length}) — no associated token
                </div>
              </div>
              <div className="divide-y divide-border">
                {orphanedTasks.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    onStart={() => start(t.id)}
                    onStop={() => stop(t.id)}
                    onEdit={() => setTaskEditor({ kind: "edit", task: t })}
                    onDelete={() => removeTask(t.id)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {taskEditor.kind !== "closed" && (
        <TaskEditor
          mode={taskEditor.kind}
          initial={taskEditor.kind === "edit" ? taskEditor.task : undefined}
          initialTokenId={taskEditor.kind === "create" ? taskEditor.tokenId : undefined}
          tokens={tokens}
          onCreateToken={createToken}
          onCancel={() => setTaskEditor({ kind: "closed" })}
          onSubmit={async (body) => {
            if (taskEditor.kind === "create") {
              await createTask(body);
            } else if (taskEditor.kind === "edit") {
              await updateTask(taskEditor.task.id, body);
            }
            setTaskEditor({ kind: "closed" });
          }}
        />
      )}

      {tokenEditor.kind !== "closed" && (
        <TokenEditor
          mode={tokenEditor.kind}
          initial={tokenEditor.kind === "edit" ? tokenEditor.token : undefined}
          onCancel={() => setTokenEditor({ kind: "closed" })}
          onSubmit={async (body) => {
            if (tokenEditor.kind === "create") {
              await createToken(body);
            } else if (tokenEditor.kind === "edit") {
              await updateToken(tokenEditor.token.id, body);
            }
            setTokenEditor({ kind: "closed" });
          }}
        />
      )}
    </main>
  );
}
